import React from "react";
import { styled } from "@mui/material";
import { ProcessedDataTableHeadersResponse } from "graphql/types/graphql";
import MDTypography from "../../../../../components/MDTypography";
import MDBox from "../../../../../components/MDBox";
import ManualSegmentationModal from "./ManualSegmentationModal";
import SegmentationItem from "./SegmentationItem";
import SegmentationControls from "./SegmentationControls";
import { MAX_SEGMENTATIONS } from "../../common/constants";
import { useSegmentationManagement } from "../../hooks/useSegmentationManagement";
import { Segment, SegmentationMethod } from "../../types/types";
import { useHeadroomContext } from "../../context/HeadRoomProvider";

interface Column {
  name: string;
  isNumeric: boolean;
  min?: number;
  max?: number;
}

interface ColumnRange {
  isNumeric: boolean;
  min?: number;
  max?: number;
}

interface CustomerSegmentationCardProps {
  processedData: ProcessedDataTableHeadersResponse;
  segmentationTypes: string[];
  isCollapsed?: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
}

const CustomerSegmentationCard: React.FC<CustomerSegmentationCardProps> = ({
  processedData,
  segmentationTypes,
  isCollapsed = false,
  setIsCollapsed,
}) => {
  const { tableName } = useHeadroomContext();

  const columnRanges = React.useMemo(() => {
    const ranges =
      processedData?.columns?.reduce((acc, col: Column) => {
        acc[col.name] = { isNumeric: col.isNumeric, min: col.min, max: col.max };
        return acc;
      }, {} as Record<string, ColumnRange>) || {};

    return ranges;
  }, [processedData]);

  const {
    segmentGroups,
    modalOpen,
    localIsCollapsed,
    handleAddSegment,
    handleRemoveSegment,
    handleTypeChange,
    handleMethodChange,
    handleModalClose,
    handleCreateSegments,
    toggleCollapse,
    shouldDisableSwitch,
    handleEdit,
  } = useSegmentationManagement({
    segmentationTypes,
    tableName,
    isCollapsed,
    setIsCollapsed,
    columnRanges,
  });

  const activeGroup = segmentGroups.find((group) => group.index === modalOpen);

  return (
    <StyledCard isCollapsed={localIsCollapsed}>
      <MDBox display="flex" justifyContent="flex-start" alignItems="center" p={0.5}>
        <MDTypography variant="h6" fontWeight="bold">
          Customer Segmentation
        </MDTypography>
      </MDBox>
      {!localIsCollapsed && (
        <MDBox mt={0}>
          {segmentGroups.map((group) => {
            const usedTypes = segmentGroups.filter((g) => g.index !== group.index && g.type).map((g) => g.type);
            return (
              <SegmentationItem
                key={group.index}
                index={group.index}
                type={group.type}
                method={group.method}
                segments={group.segments}
                segmentationTypes={segmentationTypes}
                usedTypes={usedTypes}
                columnRanges={columnRanges}
                onTypeChange={handleTypeChange}
                onMethodChange={handleMethodChange}
                onRemove={handleRemoveSegment}
                onEdit={handleEdit}
                canRemove={segmentGroups.length > 1}
                isDisabled={shouldDisableSwitch(group.index)}
              />
            );
          })}
          <SegmentationControls
            count={segmentGroups.length}
            maxCount={MAX_SEGMENTATIONS}
            onAdd={handleAddSegment}
            onRemove={() => handleRemoveSegment(segmentGroups[segmentGroups.length - 1]?.index)}
            canRemove={segmentGroups.length > 1}
          />
        </MDBox>
      )}
      {activeGroup &&
        (() => {
          const column = activeGroup.type && columnRanges[activeGroup.type];
          const isValidColumn =
            activeGroup.type && column?.isNumeric && typeof column.min === "number" && typeof column.max === "number";
          if (!isValidColumn) {
            handleModalClose();
            return null;
          }
          return (
            <ManualSegmentationModal
              key={activeGroup.index}
              open={modalOpen === activeGroup.index}
              onClose={handleModalClose}
              onCreateSegments={(segments: Segment[], method: SegmentationMethod) =>
                handleCreateSegments(segments, activeGroup.index, method)
              }
              segmentIndex={activeGroup.index}
              initialSegments={activeGroup.segments}
              columnRanges={columnRanges}
              type={activeGroup.type}
            />
          );
        })()}
    </StyledCard>
  );
};

const StyledCard = styled(MDBox)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(1.5),
  boxShadow: theme.shadows[3],
  transition: theme.transitions.create(["height", "padding-top"], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: { isCollapsed: true },
      style: {
        height: "auto",
        overflow: "hidden",
        padding: theme.spacing(0),
      },
    },
    {
      props: { isCollapsed: false },
      style: {
        height: "360px",
        maxHeight: "360px",
        overflow: "auto",
        padding: theme.spacing(1),
      },
    },
  ],
}));

export default React.memo(CustomerSegmentationCard);
